import { hasValidTrackingReference, stripVersionPrefix } from "./routes";

const TRACKING_AUTH_TOKEN_KEY = "tracking_auth_token";
const TRACKING_AUTH_PHONE_KEY = "tracking_auth_phone";
const TRACKING_AUTH_PREVIOUS_TOKEN_KEY = "tracking_auth_previous_token";
const TRACKING_AUTH_PHONE_CONFIRMATION_KEY =
  "tracking_auth_phone_confirmation";

const normalizeAuthKey = (authKey) => {
  const normalizedAuthKey = String(authKey || "").trim();

  if (
    !normalizedAuthKey ||
    normalizedAuthKey.toLowerCase() === "null" ||
    normalizedAuthKey.toLowerCase() === "undefined"
  ) {
    return null;
  }

  return normalizedAuthKey;
};

export const normalizeTrackingPhone = (phone) => {
  const normalizedValue = String(phone || "").trim();
  const lowerValue = normalizedValue.toLowerCase();

  if (
    !normalizedValue ||
    lowerValue === "null" ||
    lowerValue === "undefined"
  ) {
    return null;
  }

  const digits = normalizedValue.replace(/\D/g, "");
  if (digits.length < 10) {
    return null;
  }

  return digits.slice(-10);
};

export const isTrackingPath = (pathname) => {
  const normalizedPath =
    stripVersionPrefix(pathname).replace(/\/+$/, "").toLowerCase() || "/";

  return normalizedPath === "/tracking";
};

export const getTrackingAuthKey = (pathname, searchParams) => {
  if (
    !isTrackingPath(pathname) ||
    !hasValidTrackingReference(searchParams?.get?.("rid"))
  ) {
    return null;
  }

  return normalizeAuthKey(searchParams?.get?.("auth_key"));
};

export const getSearchWithoutAuthKey = (searchParams) => {
  const sanitizedParams = new URLSearchParams(searchParams?.toString?.() || "");
  sanitizedParams.delete("auth_key");
  const queryString = sanitizedParams.toString();

  return queryString ? `?${queryString}` : "";
};

export const getTrackingAuthFallbackQuery = (searchParams) => {
  const fallbackParams = new URLSearchParams(
    searchParams?.toString?.() || "",
  );
  fallbackParams.delete("auth_key");
  fallbackParams.set("nextTarget", "tracking");

  return fallbackParams.toString();
};

export const rememberTrackingAuthFallback = (searchParams) => {
  const fallbackQuery = getTrackingAuthFallbackQuery(searchParams);
  sessionStorage.setItem("initialQueryParams", fallbackQuery);

  return fallbackQuery;
};

export const getPhoneFromAuthKey = (authKey) => {
  const normalizedAuthKey = normalizeAuthKey(authKey);

  if (!normalizedAuthKey) {
    return null;
  }

  try {
    const payloadSegment = normalizedAuthKey.split(".")[1];
    if (!payloadSegment) {
      return null;
    }

    const base64Payload = payloadSegment
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payloadSegment.length / 4) * 4, "=");
    // The claim is only a convenience for prefilling OTP login. Authorization
    // still depends entirely on the backend validating the signed token.
    const payload = JSON.parse(window.atob(base64Payload));
    return normalizeTrackingPhone(payload?.phone);
  } catch {
    return null;
  }
};

export const rememberTrackingAuth = (authKey) => {
  const normalizedAuthKey = normalizeAuthKey(authKey);

  if (!normalizedAuthKey) {
    return;
  }

  const currentToken =
    normalizeAuthKey(sessionStorage.getItem("access_token")) ||
    normalizeAuthKey(localStorage.getItem("access_token"));
  const rememberedToken = normalizeAuthKey(
    sessionStorage.getItem(TRACKING_AUTH_TOKEN_KEY),
  );
  const trackingAuthIsActive = Boolean(
    rememberedToken && rememberedToken === currentToken,
  );

  if (!trackingAuthIsActive) {
    if (currentToken && currentToken !== normalizedAuthKey) {
      sessionStorage.setItem(TRACKING_AUTH_PREVIOUS_TOKEN_KEY, currentToken);
    } else {
      sessionStorage.removeItem(TRACKING_AUTH_PREVIOUS_TOKEN_KEY);
    }
  }

  sessionStorage.setItem("access_token", normalizedAuthKey);
  sessionStorage.setItem(TRACKING_AUTH_TOKEN_KEY, normalizedAuthKey);

  const phone = getPhoneFromAuthKey(normalizedAuthKey);
  if (phone) {
    sessionStorage.setItem(TRACKING_AUTH_PHONE_KEY, phone);
  } else {
    sessionStorage.removeItem(TRACKING_AUTH_PHONE_KEY);
  }
};

export const hasRememberedTrackingAuth = () => {
  const rememberedToken = normalizeAuthKey(
    sessionStorage.getItem(TRACKING_AUTH_TOKEN_KEY),
  );
  const currentToken = normalizeAuthKey(
    sessionStorage.getItem("access_token"),
  );

  return Boolean(rememberedToken && rememberedToken === currentToken);
};

export const getRememberedTrackingPhone = () =>
  sessionStorage.getItem(TRACKING_AUTH_PHONE_KEY);

export const rememberTrackingPhoneConfirmation = (phone) => {
  const normalizedPhone = normalizeTrackingPhone(phone);

  if (normalizedPhone) {
    sessionStorage.setItem(
      TRACKING_AUTH_PHONE_CONFIRMATION_KEY,
      normalizedPhone,
    );
  }
};

export const requiresTrackingPhoneConfirmation = (phone) => {
  const confirmationPhone = normalizeTrackingPhone(
    sessionStorage.getItem(TRACKING_AUTH_PHONE_CONFIRMATION_KEY),
  );

  return Boolean(
    confirmationPhone && confirmationPhone === normalizeTrackingPhone(phone),
  );
};

export const clearTrackingPhoneConfirmation = () => {
  sessionStorage.removeItem(TRACKING_AUTH_PHONE_CONFIRMATION_KEY);
};

export const clearRememberedTrackingAuth = () => {
  sessionStorage.removeItem(TRACKING_AUTH_TOKEN_KEY);
  sessionStorage.removeItem(TRACKING_AUTH_PHONE_KEY);
  sessionStorage.removeItem(TRACKING_AUTH_PREVIOUS_TOKEN_KEY);
  clearTrackingPhoneConfirmation();
};

export const restorePreviousAccessToken = () => {
  const previousToken = normalizeAuthKey(
    sessionStorage.getItem(TRACKING_AUTH_PREVIOUS_TOKEN_KEY),
  );

  if (previousToken) {
    sessionStorage.setItem("access_token", previousToken);
  } else {
    sessionStorage.removeItem("access_token");
  }

  clearRememberedTrackingAuth();
  return previousToken;
};
